import { matchPath } from "react-router-dom";

import { permissionMap } from "src/permissionHandler";
import { getMenuItem } from "src/MainLayout/Sidebar/MenuList";

// `page_id` is the page key ("sync_rooms"); `UserAccessPage` types it as a number.
type AccessPage = { page_id: string | number; read?: boolean; write?: boolean };
type LandingRoute = { path: string; pageName: string };

/**
 * The same rule `ProtectedRoute` applies: required entries need `read`, except
 * explicit write-only entries such as `ads_review`. A page with no config is denied.
 */
export function canReadPage(pageName: string, pages: AccessPage[]): boolean {
  const config = permissionMap[pageName];
  if (!config) return false;
  const can = (key: string) => pages.some((page) => page.page_id === key && (config[key] === "write" ? page.write : page.read));
  const keys = Object.keys(config);
  // A config with no required page (`/live`, playable review) opens to a holder of any of them, read or write
  // (the same rule as ProtectedRoute).
  if (!keys.some((key) => config[key])) return keys.some((key) => pages.some((page) => page.page_id === key && (page.read || page.write)));
  return keys.every((key) => !config[key] || can(key));
}

// Sidebar urls in the order the sidebar shows them, collapses included.
function menuUrls(items: any[]): string[] {
  return (items || []).flatMap((item) => {
    if (!item) return [];
    const own = item.type === "item" && item.url ? [item.url] : [];
    return [...own, ...menuUrls(item.children)];
  });
}

/**
 * Where a signed-in role lands when `/` is not readable for it: the first
 * sidebar entry whose route it can read, then the first readable route
 * without parameters. `/403` only when the role can read nothing at all.
 */
export function firstReadablePath(pages: AccessPage[], routes: LandingRoute[]): string {
  const readable = (url: string) => {
    const pathname = url.split("?")[0];
    const route = routes.find((r) => matchPath({ path: r.path, end: true }, pathname));
    return !!route && canReadPage(route.pageName, pages);
  };

  const fromMenu = menuUrls(getMenuItem(pages)).find(readable);
  if (fromMenu) return fromMenu;

  const fromRoutes = routes.find((r) => !r.path.includes(":") && !r.path.includes("*") && canReadPage(r.pageName, pages));
  return fromRoutes ? fromRoutes.path : "/403";
}
